import { useState, useCallback, useRef } from 'react';
import { GoogleMap, useJsApiLoader, OverlayView, Polyline, InfoWindow } from '@react-google-maps/api';
import './RunMapModal.css';

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';

const STOP_COLORS = {
  pending:  { customer: '#F57C00', warehouse: '#1565C0' },
  done:     { customer: '#2E7D32', warehouse: '#2E7D32' },
  failed:   { customer: '#C62828', warehouse: '#C62828' },
  skipped:  { customer: '#757575', warehouse: '#757575' },
};

function stopColor(stop) {
  if (stop.is_depot) return '#37474F';
  const palette = STOP_COLORS[stop.status] || STOP_COLORS.pending;
  return stop.stop_type === 'WAREHOUSE' ? palette.warehouse : palette.customer;
}

function markerLabel(stop) {
  if (stop.is_depot) return '🏭';
  return stop.sequence_no;
}

function decodePolyline(encoded) {
  if (!encoded) return [];
  const points = [];
  let index = 0, lat = 0, lng = 0;
  while (index < encoded.length) {
    let b, shift = 0, result = 0;
    do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lat += result & 1 ? ~(result >> 1) : result >> 1;
    shift = 0; result = 0;
    do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lng += result & 1 ? ~(result >> 1) : result >> 1;
    points.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return points;
}

// Straight-line fallback — used for old runs or when polyline is absent
function buildSequencePath(stops) {
  return [...stops]
    .filter(s => s.lat && s.lng)
    .sort((a, b) => a.sequence_no - b.sequence_no)
    .map(s => ({ lat: parseFloat(s.lat), lng: parseFloat(s.lng) }));
}

export default function RunMapModal({ run, stops, onClose }) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: MAPS_KEY,
    id: 'lh-gmap',
  });

  const mapRef     = useRef(null);
  const listRefs   = useRef({});
  const [activeStop, setActiveStop] = useState(null);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    const geoStops = stops.filter(s => s.lat && s.lng);
    if (geoStops.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    geoStops.forEach(s => bounds.extend({ lat: parseFloat(s.lat), lng: parseFloat(s.lng) }));
    map.fitBounds(bounds, 60);
  }, [stops]);

  const handleMarkerClick = (stop) => {
    setActiveStop(stop);
    const el = listRefs.current[stop.id];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const handleRowClick = (stop) => {
    setActiveStop(stop);
    if (mapRef.current && stop.lat && stop.lng) {
      mapRef.current.panTo({ lat: parseFloat(stop.lat), lng: parseFloat(stop.lng) });
    }
  };

  // Full road-accurate route stored by backend (covers all stops including warehouse pickups)
  const routePath    = decodePolyline(run.route_polyline);
  // Straight-line fallback for old runs that were generated before this fix
  const fallbackPath = routePath.length === 0 ? buildSequencePath(stops) : [];

  return (
    <div className="rmm-overlay" onClick={onClose}>
      <div className="rmm-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="rmm-header">
          <div className="rmm-header-left">
            <span className="rmm-run-label">{run.label}</span>
            {run.delivery_window && <span className="rmm-badge rmm-badge-window">{run.delivery_window}</span>}
            <span className={`rmm-badge rmm-badge-status rmm-status-${run.status}`}>{run.status}</span>
            <span className="rmm-stop-count">{stops.length} stop{stops.length !== 1 ? 's' : ''}</span>
          </div>
          <button className="rmm-close" onClick={onClose}>✕</button>
        </div>

        <div className="rmm-body">
          {/* Left: stop list */}
          <div className="rmm-list">
            {stops.map(stop => (
              <div
                key={stop.id}
                ref={el => listRefs.current[stop.id] = el}
                className={`rmm-stop-row ${activeStop?.id === stop.id ? 'rmm-stop-active' : ''} rmm-stop-${stop.status}`}
                onClick={() => handleRowClick(stop)}
              >
                <div className="rmm-seq" style={{ background: stopColor(stop) }}>
                  {stop.is_depot ? '🏭' : stop.sequence_no}
                </div>
                <div className="rmm-stop-info">
                  {stop.stop_type === 'WAREHOUSE' ? (
                    <span className="rmm-order-no">
                      {stop.warehouse_name}{stop.is_depot ? ' (Start)' : ''}
                    </span>
                  ) : (
                    <>
                      <span className="rmm-order-no">{stop.order_no}</span>
                      <span className="rmm-cust-name">{stop.customer_name}</span>
                    </>
                  )}
                  <span className="rmm-address">{stop.delivery_address}</span>
                </div>
                <div className={`rmm-stop-status rmm-stop-status-${stop.status}`} />
              </div>
            ))}
          </div>

          {/* Right: map */}
          <div className="rmm-map-wrap">
            {loadError && <div className="rmm-map-err">Failed to load Google Maps</div>}
            {!isLoaded && !loadError && <div className="rmm-map-loading">Loading map…</div>}
            {isLoaded && (
              <GoogleMap
                mapContainerClassName="rmm-map"
                zoom={12}
                center={{ lat: 12.9716, lng: 77.5946 }}
                onLoad={onMapLoad}
                options={{
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                  zoomControlOptions: { position: window.google.maps.ControlPosition.RIGHT_CENTER },
                }}
              >
                {/* Road-accurate full route from stored polyline */}
                {routePath.length > 1 && (
                  <Polyline
                    path={routePath}
                    options={{ strokeColor: '#F57C00', strokeWeight: 3.5, strokeOpacity: 0.85 }}
                  />
                )}

                {/* Straight-line fallback for runs generated before this fix */}
                {fallbackPath.length > 1 && (
                  <Polyline
                    path={fallbackPath}
                    options={{ strokeColor: '#F57C00', strokeWeight: 2, strokeOpacity: 0.45,
                               strokeDashArray: '6 4' }}
                  />
                )}

                {/* Stop markers */}
                {stops.filter(s => s.lat && s.lng).map(stop => (
                  <OverlayView
                    key={stop.id}
                    position={{ lat: parseFloat(stop.lat), lng: parseFloat(stop.lng) }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <div
                      className={`rmm-marker ${activeStop?.id === stop.id ? 'rmm-marker-active' : ''} ${stop.is_depot ? 'rmm-marker-depot' : ''}`}
                      style={{ background: stopColor(stop) }}
                      onClick={() => handleMarkerClick(stop)}
                      title={stop.stop_type === 'WAREHOUSE' ? stop.warehouse_name : stop.order_no}
                    >
                      {markerLabel(stop)}
                    </div>
                  </OverlayView>
                ))}

                {/* Info window */}
                {activeStop && activeStop.lat && activeStop.lng && (
                  <InfoWindow
                    position={{ lat: parseFloat(activeStop.lat), lng: parseFloat(activeStop.lng) }}
                    onCloseClick={() => setActiveStop(null)}
                  >
                    <div className="rmm-infowin">
                      {activeStop.stop_type === 'WAREHOUSE' ? (
                        <strong>{activeStop.warehouse_name}{activeStop.is_depot ? ' — Start' : ''}</strong>
                      ) : (
                        <>
                          <div className="rmm-iw-orderno">{activeStop.order_no}</div>
                          <div className="rmm-iw-name">{activeStop.customer_name}</div>
                        </>
                      )}
                      <div className="rmm-iw-addr">{activeStop.delivery_address}</div>
                      {activeStop.delivery_window && (
                        <div className="rmm-iw-row">Window: {activeStop.delivery_window}</div>
                      )}
                      <div className={`rmm-iw-status rmm-iw-status-${activeStop.status}`}>
                        {activeStop.status}
                      </div>
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
